import React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

import NothingSelected from "../screens/nothing-selected.js";
import { SCREENS_FOR, HOME_FOR } from "../screens/registry.js";
import { useBuilding } from "../state/building.js";

const EASE = [0.16, 1, 0.3, 1];

/**
 * The screen stage.
 *
 * Was: a copy of `value` in local state, a boolean, and a 100ms setTimeout
 * that swapped the content halfway through a CSS class change. That raced
 * with itself - two taps inside 100ms left the state holding a screen the
 * rail no longer had selected - and the outgoing screen was replaced
 * rather than animated out, so the "close" half of it was never visible.
 *
 * AnimatePresence keeps the outgoing screen mounted until its exit
 * finishes, and `mode="popLayout"` lets the incoming one arrive over the
 * top instead of waiting. There is no local copy of `value` any more, so
 * there is nothing to fall out of step with the rail.
 */
function Screen({ value, mode = 'resident' }) {
    const reduced = useReducedMotion();
    const { alert } = useBuilding();

    /* Anything this side does not have, including "nothing chosen",
       falls back to the side's home: the welcome for residents, the
       property dashboard for staff. */
    const list = SCREENS_FOR[mode];
    const home = list.find(item => item.screen === HOME_FOR[mode]);
    const entry = list.find(item => item.screen === value) || home;
    const Current = entry ? entry.Component : NothingSelected;
    const key = `${mode}:${entry ? entry.screen : '__welcome__'}`;

    return (
        <div className="stage">
            <AnimatePresence mode="popLayout" initial={false}>
                <motion.section
                    key={key}
                    className="screen"
                    initial={reduced ? false : { opacity: 0, y: 26, scale: 0.985, filter: 'blur(8px)' }}
                    animate={{ opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }}
                    exit={reduced ? { opacity: 0 } : { opacity: 0, y: -18, scale: 0.99, filter: 'blur(6px)' }}
                    transition={{ duration: 0.55, ease: EASE }}
                >
                    <div className="screen__scroll">
                        {/* An alert the front desk sends sits above whatever a
                            resident is looking at until the desk ends it. */}
                        {mode === 'resident' && alert && <AlertStrip alert={alert} />}
                        <Current />
                    </div>
                </motion.section>
            </AnimatePresence>
        </div>
    );
}

function AlertStrip({ alert }) {
    const emergency = alert.level === 'emergency';

    return (
        <div className={`alert-strip${emergency ? '' : ' alert-strip--info'}`} role="alert">
            <span className="alert-strip__dot" aria-hidden="true" />
            <div>
                <span className="alert-strip__label">
                    {emergency ? 'Emergency alert' : 'Building advisory'} · {alert.sentAt}
                </span>
                <strong className="alert-strip__title">{alert.title}</strong>
                {alert.body && <p className="alert-strip__body">{alert.body}</p>}
            </div>
        </div>
    );
}

export default Screen;
